import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Form, Modal } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { dayLabel, formatDateTime } from '../utils/formatDate';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Chart.js draws to a <canvas>, which doesn't resolve CSS custom properties — literal values of the --mm- tokens.
const CYAN = '#12B8B2';
const SLATE = 'rgba(157,179,194,0.55)';
const MUTED = '#9DB3C2';
const LINE = 'rgba(234,242,245,0.10)';

const CHART_OPTIONS = {
  maintainAspectRatio: false,
  plugins: { legend: { labels: { color: MUTED, boxWidth: 12, boxHeight: 12 } } },
  scales: {
    x: { ticks: { color: MUTED }, grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0, color: MUTED }, grid: { color: LINE } },
  },
};

const KIND_LABEL = {
  entrada: 'Entrada',
  ajuste: 'Conteo físico',
  despacho: 'Despacho',
  despacho_editado: 'Despacho editado',
  despacho_eliminado: 'Despacho eliminado',
  devolucion: 'Devolución',
};

const SHORT_LIST = 12;

async function send(url, method, body) {
  const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo guardar.');
  return data;
}

// The cylinder seal stock (GET /api/seals, backend/utils/seals.js): every cylinder that leaves on a despacho takes one,
// and the seals follow the despacho after that (edits, deletions, devoluciones). Admins add what arrives, correct the
// balance with a physical count, and set the low level that alerts by email, push and WhatsApp.
const SealsPanel = () => {
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [dialog, setDialog] = useState(null); // 'restock' | 'count' | 'alert'
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch('/api/seals').then((res) => res.json()).then(setData)
      .catch((err) => console.error('❌ Error fetching seals:', err));
  }, []);
  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    const weeks = data?.weeks || [];
    return {
      labels: weeks.map((w) => `${dayLabel(w.from)}${w.partial ? ' (en curso)' : ''}`),
      datasets: [
        { label: 'Usados', data: weeks.map((w) => w.used), backgroundColor: CYAN, borderRadius: 4 },
        { label: 'Recibidos', data: weeks.map((w) => w.received), backgroundColor: SLATE, borderRadius: 4 },
      ],
    };
  }, [data]);

  const open = (kind) => {
    setError('');
    if (kind === 'alert') setForm({ lowAt: String(data?.lowAt ?? 0), alertEmails: (data?.alertEmails || []).join(', ') });
    else if (kind === 'count') setForm({ count: '', reason: '' });
    else setForm({ quantity: '', note: '' });
    setDialog(kind);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (dialog === 'restock') await send('/api/seals/restock', 'POST', { quantity: Number(form.quantity), note: form.note });
      if (dialog === 'count') await send('/api/seals/count', 'POST', { count: Number(form.count), reason: form.reason });
      if (dialog === 'alert') await send('/api/seals/settings', 'PATCH', { lowAt: Number(form.lowAt), alertEmails: form.alertEmails });
      setDialog(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const movements = data.movements || [];
  const balanceClass = data.balance < 0 || data.low ? 'text-mm-red' : 'text-mm-green';

  return (
    <div className="row mb-4">
      <div className="col-12">
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: '8px' }}>
              <Card.Title className="mb-0">Sellos de Cilindros</Card.Title>
              <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                <Button size="sm" variant="info" onClick={() => open('restock')}>Agregar sellos</Button>
                <Button size="sm" variant="outline-light" onClick={() => open('count')}>Conteo físico</Button>
                <Button size="sm" variant="outline-light" onClick={() => open('alert')}>Alerta</Button>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>En existencia</div>
                <div className={balanceClass} style={{ fontSize: '2.6rem', fontWeight: 700, lineHeight: 1.1 }}>{data.balance}</div>
                {data.low && <div className="text-mm-red" style={{ fontSize: '0.85rem' }}>Por debajo del nivel de alerta</div>}
                {data.balance < 0 && <div className="text-muted" style={{ fontSize: '0.8rem' }}>Negativo: salieron más cilindros que sellos registrados — haz un conteo físico.</div>}
                <div className="mt-2" style={{ fontSize: '0.9rem' }}>
                  <div>Alerta en: <strong>{data.lowAt > 0 ? data.lowAt : 'sin alerta'}</strong></div>
                  <div>Uso promedio: <strong>{data.avgPerDay ? `${data.avgPerDay} por día` : 'sin datos'}</strong> <span className="text-muted">(últimas 4 semanas)</span></div>
                  <div>Alcanzan para: <strong>{data.daysLeft == null ? '—' : `${Math.max(data.daysLeft, 0)} días aprox.`}</strong></div>
                  <div>Última entrada: <strong>{data.lastRestock ? `${data.lastRestock.quantity} el ${formatDateTime(data.lastRestock.at)}` : '—'}</strong></div>
                </div>
              </div>
              <div className="col-md-8 mb-3" style={{ height: 220 }}>
                <Bar data={chartData} options={CHART_OPTIONS} />
              </div>
            </div>

            <Table size="sm" striped className="mb-1">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Movimiento</th>
                  <th>Nota / Razón</th>
                  <th>Por</th>
                  <th className="text-right">Cambio</th>
                  <th className="text-right">Existencia</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 && (
                  <tr><td colSpan={6} className="text-muted">Sin movimientos todavía. Agrega los sellos que tienes para empezar.</td></tr>
                )}
                {(showAll ? movements : movements.slice(0, SHORT_LIST)).map((m) => (
                  <tr key={m._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(m.at)}</td>
                    <td>{KIND_LABEL[m.kind] || m.kind}</td>
                    <td>{[m.noteNumber, m.reason].filter(Boolean).join(' · ') || '—'}</td>
                    <td>{m.by || '—'}</td>
                    <td className={`text-right ${m.delta > 0 ? 'text-mm-green' : 'text-mm-red'}`}>{m.delta > 0 ? `+${m.delta}` : m.delta}</td>
                    <td className="text-right">{m.balanceAfter}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {movements.length > SHORT_LIST && (
              <Button size="sm" variant="link" className="p-0" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Ver menos' : `Ver los ${movements.length} movimientos`}
              </Button>
            )}
            <div>
              <small className="text-muted">
                Cada cilindro despachado usa un sello. Si se edita o elimina un despacho, o los tanques regresan (devolución o
                entrega incompleta), los sellos vuelven a la existencia. Los despachos de antes de que existiera este control no
                cuentan.
              </small>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={!!dialog} onHide={() => setDialog(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {dialog === 'restock' && 'Agregar sellos'}
            {dialog === 'count' && 'Conteo físico de sellos'}
            {dialog === 'alert' && 'Alerta de sellos bajos'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dialog === 'restock' && (
            <>
              <Form.Group>
                <Form.Label>Cantidad recibida</Form.Label>
                <Form.Control type="number" min="1" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} autoFocus />
              </Form.Group>
              <Form.Group>
                <Form.Label>Nota (opcional)</Form.Label>
                <Form.Control value={form.note} placeholder="Proveedor, factura…" onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </Form.Group>
            </>
          )}
          {dialog === 'count' && (
            <>
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                La existencia pasa a ser lo que contaste. Ahora el sistema dice {data.balance}.
              </p>
              <Form.Group>
                <Form.Label>Sellos contados</Form.Label>
                <Form.Control type="number" min="0" step="1" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} autoFocus />
              </Form.Group>
              <Form.Group>
                <Form.Label>Razón del ajuste</Form.Label>
                <Form.Control value={form.reason} placeholder="Conteo de fin de mes, sellos dañados…" onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </Form.Group>
            </>
          )}
          {dialog === 'alert' && (
            <>
              <Form.Group>
                <Form.Label>Avisar cuando queden (o menos)</Form.Label>
                <Form.Control type="number" min="0" step="1" value={form.lowAt} onChange={(e) => setForm({ ...form, lowAt: e.target.value })} autoFocus />
                <Form.Text className="text-muted">0 = sin alerta. Se avisa una vez; vuelve a avisar después de agregar sellos.</Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Correos que reciben la alerta</Form.Label>
                <Form.Control value={form.alertEmails} placeholder="nombre@caribbeangas.com, otro@caribbeangas.com" onChange={(e) => setForm({ ...form, alertEmails: e.target.value })} />
                <Form.Text className="text-muted">También llega como notificación a los administradores y por WhatsApp al dueño.</Form.Text>
              </Form.Group>
            </>
          )}
          {error && <div className="text-mm-red mt-2">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialog(null)}>Cancelar</Button>
          <Button variant="info" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SealsPanel;
